import { clientApi } from '@phoenix/clientApi';
import * as formHelper from '@phoenix/helpers/form-helper';
import { configureVeeValidate } from '@phoenix/helpers/form-helper';
import chinesePiplMixin from '@phoenix/mixins/chinesePiplMixin';
import { extend, filter, forEach, get, includes, set } from 'lodash-es';
import { parse as queryStringParse } from 'query-string';
import { load } from 'recaptcha-v3';
import { mapActions, mapState } from 'vuex';

configureVeeValidate();

export default {
  mixins: [chinesePiplMixin],

  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    action: {
      type: String,
      required: false,
      default: null,
    },
    method: {
      type: String,
      default: 'post',
      validator(value) {
        return ['post', 'get', 'put', 'delete'].indexOf(value) !== -1;
      },
    },
    prefillEnabled: {
      type: Boolean,
      default: false,
    },
    redirectUrl: {
      type: String,
      required: false,
      default: '',
    },
    captchaEnabled: {
      type: Boolean,
      default: false,
    },
    fillCustomerInfo: {
      type: Boolean,
      default: true,
    },
    shouldDisplayRequiredText: {
      type: Boolean,
      default: true,
    },
    customSubmitText: {
      type: String,
      default: '',
    },
    customCancelText: {
      type: String,
      default: '',
    },
    onSuccessCallback: {
      type: Function,
      default: () => {},
    },
    onFailCallback: {
      type: Function,
      default: () => {},
    },
    onCancelCallback: {
      type: Function,
      default: () => {},
    },
    showErrorsInToast: {
      type: Boolean,
      default: true,
    },
    showSuccessInToast: {
      type: Boolean,
      default: true,
    },
    shouldDisplaySubmitButton: {
      type: Boolean,
      default: true,
    },
    shouldDisplayCancelButton: {
      type: Boolean,
      default: false,
    },
    termsLink: {
      type: String,
      default: '',
    },
    terms: {
      type: String,
      default: '',
    },
    privacyLink: {
      type: String,
      default: '',
    },
    formType: {
      type: String,
      default: '',
    },
    steps: {
      type: Number,
      default: 0,
    },
    currentStep: {
      type: Number,
      default: 1,
    },
    shouldSaveInLocalStorage: {
      type: Boolean,
      default: false,
    },
    shouldResetFormOnSubmit: {
      type: Boolean,
      default: false,
    },
    koreanFormType: {
      type: String,
      default: '',
    },
    shouldIncludeBrowsingHistory: {
      type: Boolean,
      default: false,
    },
    refreshPing: {
      type: Boolean,
      default: false,
    },
    isSubmitDisabled: {
      type: Boolean,
      default: false,
    },
    blacklistedFieldsFromPrefillFromQuery: {
      type: Array,
      default: () => [],
    },
    beforeSubmit: {
      type: Function,
      default: () => true,
    },
    shouldValidateImmediately: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'formLoaded', 'changeStep'],

  data() {
    return {
      isLoading: false,
      recaptcha: null,
      captchaError: '',
      mainError: null,
      fileTypes: window.Phoenix.config.FORMS.CUSTOM.FILE_FIELDS,
      koreanHasAccepted: false,
    };
  },

  computed: {
    ...mapState('form', ['csrfToken', 'error']),

    ...mapState('currentUser', ['customerInfo']),

    formValues() {
      return formHelper.convertFormDataToData(this.modelValue);
    },

    isCaptchaEnabled() {
      return this.captchaEnabled && window.Phoenix.config.CAPTCHA_CONFIG_V3_ENABLE;
    },

    nextStepText() {
      return this.$t('Form.next');
    },

    previousStepText() {
      return this.$t('Form.previous');
    },

    finalSubmitText() {
      return this.customSubmitText !== '' ? this.customSubmitText : this.$t('Form.submit');
    },

    submitText() {
      return this.hasSteps && !this.isFinalStep ? this.nextStepText : this.finalSubmitText;
    },

    cancelText() {
      return this.customCancelText !== '' ? this.customCancelText : this.$t('Form.cancel');
    },

    mainErrors() {
      return get(this, ['mainError']) || this.error;
    },

    isDisabled() {
      if (this.shouldDisplayPIPLConsentCheckboxes) {
        return !this.hasConsentedToChinesePipl;
      }
      if (this.shouldDisplayKoreanGDPR) {
        return !this.koreanHasAccepted;
      }
      return this.isSubmitDisabled;
    },

    submitClass() {
      return ['btn btn--primary', { 'btn--disabled': this.isDisabled }];
    },

    cancelClass() {
      return ['btn btn--default', { 'btn--disabled': this.isDisabled }];
    },

    shouldShowFormActions() {
      return (
        this.shouldDisplaySubmitButton ||
        this.shouldDisplayCancelButton ||
        this.termsLink ||
        !!this.$slots['footer-actions-text'] ||
        !!this.$slots['form-actions']
      );
    },

    shouldDisplayKoreanGDPR() {
      return window.Phoenix.currentCountry === 'KR' && this.koreanFormType !== '';
    },

    shouldDisplayTermsLink() {
      return (
        this.termsLink &&
        !this.shouldDisplayKoreanGDPR &&
        !this.shouldDisplayPIPLConsentCheckboxes &&
        !this.shouldDisplayPIPLStatement
      );
    },

    requiredFieldsText() {
      const textArray = ['*', this.$t('Form.required_field')];
      // PIA-8197 This is a specific rule and for this unique case, it should be done like that
      return (document.dir === 'rtl' ? textArray.reverse() : textArray).join(' ');
    },

    hasSteps() {
      return this.steps > 0;
    },

    isFinalStep() {
      return this.currentStep === this.steps;
    },

    shouldDisplayPreviousButton() {
      return this.hasSteps && this.currentStep > 1;
    },

    localStorageId() {
      return `form-${this.modelValue.blockId.value}`;
    },

    fileFields() {
      return filter(this.modelValue, (field) => this.isFileField(field));
    },
  },

  provide() {
    return {
      shouldValidateImmediately: this.shouldValidateImmediately,
    };
  },

  created() {
    this.isLoading = this.fillCustomerInfo || (this.hasSteps && this.shouldSaveInLocalStorage);
  },

  async mounted() {
    // we load the customer information in all cases because the CSRF token will be set there
    await this.loadCustomerInfo(this.refreshPing);
    if (this.fillCustomerInfo) {
      formHelper.setCustomerCountryInForm(this.modelValue, this.customerInfo);
      this.setCustomerCbdtConsentInForm(this.customerInfo);
      this.$emit('formLoaded');
    } else if (get(this.modelValue, ['country', 'value']) === null) {
      formHelper.setCustomerCountryInForm(this.modelValue, { country: window.Phoenix.currentCountry });
    }

    if (this.isCaptchaEnabled) {
      await this.loadRecaptchaV3();
    }

    if (this.prefillEnabled) {
      this.prefillFormFromQuery();
    }

    if (this.hasSteps && this.shouldSaveInLocalStorage) {
      this.$nextTick(() => {
        this.prefillFromLocalStorage();
      });
    } else {
      this.isLoading = false;
    }

    if (this.submitText === '') {
      this.submitText = this.$t('Form.submit');
    }
  },

  methods: {
    ...mapActions('currentUser', ['loadCustomerInfo']),

    /**
     * Clear veeValidate error bag (can be called from outside)
     */
    clearErrors() {
      //this.$validator.errors.clear();
      this.captchaError = '';
      this.mainError = '';
    },

    async submit() {
      if (!(await this.beforeSubmit())) {
        return false;
      }

      this.captchaError = '';
      this.mainError = '';
      this.cleanFieldsError();

      Toolkit.helpers?.buttons?.addLoadingState(this.$refs.submitButton);

      this.setValues(this.formValues);
      const { valid } = await this.validate();
      if (!valid) {
        this.showError(this.$t('Form.validations.error'));
        if (this.onFailCallback) {
          this.onFailCallback({ message: this.$t('Form.validations.error') });
        }
        return false;
      }

      if (this.hasSteps && !this.isFinalStep) {
        this.goToNextStep();
        return;
      }

      const token = await this.getTokenFromCaptcha();

      if (this.action) {
        return await this.sendForm(token);
      }

      if (this.shouldResetFormOnSubmit) {
        this.$refs.form.reset();
      }

      return true;
    },

    showError(error) {
      this.removeLoader();

      if (!error) {
        return;
      }

      if (this.showErrorsInToast) {
        return window.Phoenix.toaster?.notify({
          type: 'danger',
          text: error,
        });
      }

      this.mainError = error;
    },

    getTokenFromCaptcha() {
      if (!this.isCaptchaEnabled) {
        return null;
      }

      return this.recaptcha
        .execute(this.formType || 'default')
        .then((token) => token)
        .catch(() => false);
    },

    async sendForm(captchaToken) {
      if (this.isDisabled) {
        this.showError('Unknown error.');
        return false;
      }
      try {
        const headers = clientApi.client.defaults.headers;
        let data = extend(
          { captchaToken },
          // BrowsingHistory should be the last field in the submission preview and email
          extend(this.formValues, this.getChinesePiplFormData(), this.getBrowsingHistoryAsFormData()),
        );

        // If the form contains file(s) to submit
        if (this.fileFields.length > 0) {
          headers['Content-Type'] = 'multipart/form-data';
          const formData = new FormData();
          forEach(data, (value, key) => {
            if (this.isFileField(this.modelValue[key]) && this.modelValue.value) {
              formData.append(key, value);
            } else if (Array.isArray(value)) {
              value.forEach((itemValue, itemKey) => formData.append(`${key}[${itemKey}]`, itemValue));
            } else {
              formData.append(key, value);
            }
            data = formData;
          });
        }

        const response = await clientApi.request({
          method: this.method,
          url: this.action,
          data,
          headers,
        });

        if (!response.data.status || response.data.status !== true) {
          const error = new Error('Invalid response');
          error.response = response;
          throw error;
        }

        if (this.hasSteps && this.shouldSaveInLocalStorage) {
          localStorage.removeItem(this.localStorageId);
        }

        if (this.onSuccessCallback) {
          this.onSuccessCallback(response);
        }

        if (this.redirectUrl !== '') {
          if (response.data.message && response.data.message.length > 0) {
            window.Phoenix.notification.create(response.data.message, 'success');
          }
          window.location.href = this.redirectUrl;
        } else {
          if (this.showSuccessInToast && response.data.message && response.data.message.length > 0) {
            window.Phoenix.toaster?.notify({
              type: 'success',
              text: response.data.message,
            });
          }

          if (this.shouldResetFormOnSubmit) {
            this.$refs.form.reset();
            if (this.hasSteps) {
              this.changeStep(1);
            }
          }

          this.removeLoader();
        }
      } catch (error) {
        this.removeLoader();

        if (this.onFailCallback) {
          this.onFailCallback(error);
        }

        const responseData = error.response ? error.response.data : error;

        if (responseData.errors) {
          this.setErrors(responseData.errors);
        }

        if (responseData.message !== undefined) {
          // If the message is empty but set, it means that we don't want to display it.
          if (responseData.message.length > 0) {
            this.showError(responseData.message);
          }
        } else {
          this.showError('Unknown error.');
        }

        return false;
      }

      return true;
    },

    prefillFormFromQuery() {
      const queryString = queryStringParse(window.location.search, {
        arrayFormat: 'bracket',
      });

      for (const key in queryString) {
        if (this.blacklistedFieldsFromPrefillFromQuery.includes(key)) {
          continue;
        }

        const field = formHelper.findFieldInFormByName(this.modelValue, key);

        if (field) {
          set(field, ['value'], queryString[key]);
        }
      }

      this.$emit('update:modelValue', this.modelValue);
    },

    prefillFromLocalStorage() {
      const storedData = JSON.parse(localStorage.getItem(this.localStorageId));

      if (storedData) {
        for (const storedValue in storedData.values) {
          if (this.modelValue[storedValue] !== undefined) {
            set(this.modelValue, [storedValue, 'value'], storedData.values[storedValue]);
          }
        }
        this.$emit('update:modelValue', this.modelValue);
        this.changeStep(storedData.step, false);
      }

      this.isLoading = false;
    },

    cleanFieldsError() {
      for (const key in this.modelValue) {
        set(this.modelValue, [key, 'errors'], '');
      }
    },

    removeLoader() {
      Toolkit.helpers?.buttons?.removeLoadingState(this.$refs.submitButton);
    },

    async loadRecaptchaV3() {
      this.recaptcha = await load(window.Phoenix.config.CAPTCHA_CONFIG_V3_SITE_KEY, {
        useRecaptchaNet: true,
        renderParameters: { badge: 'bottomleft' },
      });
    },

    setErrors(errors) {
      this.captchaError = formHelper.convertErrorsInFormData(this.modelValue, errors);
      this.$emit('update:modelValue', this.modelValue);
    },

    changeStep(step, scrollToNavigation = true) {
      this.$emit('changeStep', { step, scrollToNavigation });
    },

    goToNextStep() {
      if (this.isFinalStep) {
        return;
      }

      const nextStep = this.currentStep + 1;

      if (this.shouldSaveInLocalStorage) {
        localStorage.setItem(
          this.localStorageId,
          JSON.stringify({
            step: nextStep,
            values: this.formValues,
          }),
        );
      }

      this.changeStep(nextStep);

      this.removeLoader();
    },

    goToPreviousStep() {
      this.changeStep(this.currentStep - 1);
    },

    onKoreanGDPRChange(accepted, newsletter = null) {
      this.koreanHasAccepted = accepted;

      if (newsletter !== null) {
        set(this.modelValue, 'newsletter.value', newsletter);
        this.$emit('update:modelValue', this.modelValue);
      }
    },

    onConsentValuesLoaded() {
      this.$emit('onConsentValuesLoaded');
    },

    isFileField(field) {
      return field?.type !== undefined && includes(this.fileTypes, field.type);
    },

    getBrowsingHistoryAsFormData() {
      return this.shouldIncludeBrowsingHistory &&
        window.Phoenix.browsingHistory &&
        window.Phoenix.browsingHistory.length
        ? { browsingHistory: window.Phoenix.browsingHistory }
        : {};
    },
  },
};
